import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../services/api';

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#1a1a2e',
    borderRadius: '8px',
    padding: '32px',
    width: '400px',
    maxWidth: '90%',
    border: '1px solid #2d2d44',
  },
  title: {
    color: '#ccd6f6',
    fontSize: '24px',
    marginBottom: '8px',
    textAlign: 'center' as const,
  },
  subtitle: {
    color: '#8892b0',
    fontSize: '14px',
    marginBottom: '24px',
    textAlign: 'center' as const,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  label: {
    color: '#8892b0',
    fontSize: '14px',
  },
  input: {
    padding: '12px',
    backgroundColor: '#0a0a14',
    border: '1px solid #2d2d44',
    borderRadius: '4px',
    color: '#ccd6f6',
    fontSize: '16px',
  },
  button: {
    padding: '12px 24px',
    backgroundColor: '#64ffda',
    color: '#0a0a14',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600,
    marginTop: '8px',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    color: '#ff6b6b',
    fontSize: '14px',
    textAlign: 'center' as const,
  },
  logoutLink: {
    color: '#8892b0',
    fontSize: '14px',
    textAlign: 'center' as const,
    marginTop: '16px',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};

export function ChangePasswordModal() {
  const { changePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const isValid = currentPassword && newPassword && confirmPassword && newPassword === confirmPassword;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2 style={styles.title}>Password Reset Required</h2>
        <p style={styles.subtitle}>
          Your password needs to be changed before you can continue.
        </p>
        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Current Password</label>
            <input
              type="password"
              style={styles.input}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              disabled={isLoading}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>New Password</label>
            <input
              type="password"
              style={styles.input}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={isLoading}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirm New Password</label>
            <input
              type="password"
              style={styles.input}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={isLoading}
            />
          </div>
          {error && <p style={styles.error}>{error}</p>}
          <button
            type="submit"
            style={{
              ...styles.button,
              ...(isLoading || !isValid ? styles.buttonDisabled : {}),
            }}
            disabled={isLoading || !isValid}
          >
            {isLoading ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
        <p style={styles.logoutLink} onClick={logout}>
          Logout instead
        </p>
      </div>
    </div>
  );
}
